import { getAlertsByRootCauseWithPagination } from '@src/api/aiAlerts';
import { dimensionsListSelectorKeyValue } from '@src/store/common/dimensionList/selectors';
import { aiAlertsDimensionMappingSelector } from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { get } from 'lodash';
import moment from 'moment-timezone';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { formatAIAlertSummary } from '../formatter';
import { QUERY_SORT_MAPPER, SORT_ORDER_MAPPER } from '../constants';
import { AlertsPage, InfiniteScrollFilterParams } from '../types';

export const useFetchAlertsForRootCause = (
  interval,
  filterParams: InfiniteScrollFilterParams,
  sortParams: { key: string; order: string } = { key: 'eventTime', order: 'descend' },
  fetchAll: boolean = false,
  enabled: boolean = true,
) => {
  const { from, to } = interval;
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue);
  const dimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  const { key, order } = sortParams;

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isError, refetch } =
    useInfiniteQuery(
      [
        'ai-alerts-for-root-cause',
        from,
        to,
        JSON.stringify(filterParams),
        JSON.stringify(sortParams),
      ], // Serialize filterParams for proper cache invalidation
      ({ pageParam = null, signal }) => {
        // Add signal for request cancellation
        const { rootCause, dimensionName, metricType } = filterParams;
        const requestBody = {
          from: moment(from).format(),
          to: moment(to).format(),
          limit: filterParams.limit,
          cursor: pageParam, // Backend cursor for pagination
          cache: true,
          filters: {
            // can handle Filter applied in trends
            ...(rootCause && { rootCauseValue: rootCause }),
            ...(dimensionName && { rootCauseDimension: dimensionName }),
            // Handle multiple metric types (OR within group)
            ...(metricType && {
              metricType: Array.isArray(metricType) ? metricType : [metricType],
            }),
          },
          getAll: fetchAll,
          sortBy: QUERY_SORT_MAPPER[key],
          sortOrder: SORT_ORDER_MAPPER[order],
          signal, // Pass abort signal for request cancellation
        };

        return getAlertsByRootCauseWithPagination(requestBody, { signal }).then((response) => {
          const responseData = get(response, ['data'], {});
          // Alerts are in responseData.data
          const alerts = get(responseData, ['data'], []);
          const pagination = get(responseData, ['pagination'], {});

          const totalCount = get(pagination, ['totalCount'], 0);
          const nextCursor = get(pagination, ['nextCursor'], null);
          const hasMore = Boolean(get(pagination, ['hasMore'], false));
          const limit = get(pagination, ['limit'], 0);
          const cacheKey = get(pagination, ['cacheKey'], null);

          // Extract filter counts from the response
          const counts = get(responseData, ['totals'], {});
          const metricCounts = get(counts, ['metricCounts'], {});

          const formattedAlerts = alerts?.length
            ? formatAIAlertSummary(
                alerts,
                dimensionListObject as any,
                dimensionMapping[CURRENT_PRODUCT_MODE.toLowerCase()],
              )
            : [];

          return {
            alerts: formattedAlerts,
            nextCursor,
            hasNextPage: hasMore,
            total: totalCount,
            limit,
            cacheKey,
            counts, // Include counts in the response
            metricCounts, // Include metric counts separately for easier access
          } as AlertsPage;
        });
      },
      {
        getNextPageParam: (lastPage) => {
          // Accept 0 as a valid cursor (if backend uses 0 as a valid value)
          if (
            lastPage.hasNextPage &&
            lastPage.nextCursor !== null &&
            lastPage.nextCursor !== undefined
          ) {
            return lastPage.nextCursor;
          }
          return undefined;
        },
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 60 * 1000, // 1 minute stale time to prevent unnecessary refetches for same sort params
        cacheTime: 5 * 60 * 1000, // Keep cache for 5 minutes but don't use stale data
        enabled: enabled && !!interval && !!to && !!from, // Add enabled parameter control
        keepPreviousData: false, // Don't keep previous data to prevent race condition display
        refetchOnMount: false, // Don't always refetch - use cache when data is fresh
      },
    );

  // Flatten all pages into a single array
  const allAlerts = data?.pages?.flatMap((page: AlertsPage) => page.alerts) || [];
  const total = (data?.pages?.[0] as AlertsPage)?.total || 0;
  const counts = (data?.pages?.[0] as AlertsPage)?.counts || {};

  return {
    alerts: allAlerts,
    total,
    counts, // Include counts for sidebar
    hasNextPage: hasNextPage || false,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isEmpty: allAlerts.length === 0,
  };
};
